import _ from 'lodash';
import { MANAGEMENT_LOCK } from './constants';

export const getAPI = () => {
    // local api config
    const { REACT_APP_API_HOST_DEV, REACT_APP_API_HOST_PROD, REACT_APP_PORT_DEV, REACT_APP_PORT_PROD, REACT_APP_IS_DEV  } = process.env;

    // api config
    return Number(REACT_APP_IS_DEV)
    ? `https://${REACT_APP_API_HOST_DEV}:${REACT_APP_PORT_DEV}`
    : `https://${REACT_APP_API_HOST_PROD}:${REACT_APP_PORT_PROD}`;
}

// A fully-qualified public host talks to QMA over a real domain (e.g. averly.com.na
// or qm.averly.com.na) rather than a LAN device whose name is a bare host / `*.local`.
// These are reached directly over HTTPS (443) with no `.local` suffix and no `:4000`
// port appended. Matches `.com` optionally followed by a country code (`.com.na`, …).
export const isFullyQualifiedQmaHost = (hostname = '') => (
    /\.com(\.[a-z]{2})?$/i.test(String(hostname).trim())
);

// Build the base URL used for QMA local API / socket calls against a branch host.
// FQDN hosts are used as-is over HTTPS; LAN hosts keep the legacy `:4000` port.
export const getQmaBaseUrl = (hostname = '') => {
    const host = String(hostname).trim();
    if (!host) return '';
    return isFullyQualifiedQmaHost(host) ? `https://${host}` : `https://${host}:4000`;
}

// Resolve which form-builder options an organization should see.
//
// The API returns every org's form-builder options plus shared/global ones.
// Rules:
//  - Global options (no organizationId) are shared with every org.
//  - The org's own options are always included.
//  - For an option type the org has NOT authored any options of, fall back to the
//    shared options that exist for that type. The platform sign-out and transfer
//    reason lists are seeded under a single org id (not as global rows), so without
//    this per-type fallback every other org sees an empty reason dropdown.
//
// An org that HAS authored its own options for a type still sees only its own for
// that type (plus every global option), preserving per-org customisation.
export const resolveOrgFbOptions = (data = [], organizationId) => {
    const options = (Array.isArray(data) ? data : []).filter(Boolean);
    if (!organizationId) return options;

    const customizedTypeIds = new Set(
        options
            .filter((option) => option.organizationId === organizationId)
            .map((option) => option.optionTypeId)
    );

    return options.filter((option) => (
        !option.organizationId
        || option.organizationId === organizationId
        || !customizedTypeIds.has(option.optionTypeId)
    ));
};

export const toastActions = {
    "add": ["Adding", "adding", "added"], 
    "update": ["Updating", "updating", "updated"], 
    "delete": ["Deleting", "deleting", "deleted"]
}
export const isSupervisorUser = (user = {}) => (
    Number(user.isSupervisor) === 1
    || user.isSupervisor === true
    || Number(user.isAdmin) === 1
    || user.isAdmin === true
);
export const getMyActiveTicket = (tickets, activeTicketId) => {
    // find ticket where ticket.id === activeTicketId
    const activeTicket = tickets.find( a => a.id === activeTicketId && Number(a.status) === 1 );
    // stringify and parse + deep clone with lodash
    return activeTicket != null ? _.cloneDeep(activeTicket) : null;
}
export const getAllActiveTickets = (tickets) => {
    return Array.from(
        tickets.filter( a => Number(a.status) === 1 )
    );
}
export const getQueueCreated = (ticket) => (
    ticket && ticket.reissuedFromTicketId
    ? Number(ticket.originalCreated || ticket.created || 0)
    : Number(ticket.created || 0)
);

export const getTicketDisplayLabel = (ticket) => (
    ticket ? `${ticket.ticketLetter}${ticket.ticketNumber}` : null
);

export const getReissueDisplayInfo = (ticket, tickets = []) => {
    if (!ticket || !ticket.reissuedFromTicketId) {
        return null;
    }

    const reissuedFromTicket = tickets.find(data => data.id === ticket.reissuedFromTicketId);
    const originalTicketId = ticket.originalTicketId || ticket.reissuedFromTicketId;
    const originalTicket = tickets.find(data => data.id === originalTicketId);

    return {
        reissuedFromLabel: getTicketDisplayLabel(reissuedFromTicket) || 'Ticket unavailable',
        originalTicketLabel: getTicketDisplayLabel(originalTicket) || 'Ticket unavailable',
        originalCreated: Number(ticket.originalCreated || ticket.created)
    };
};

const sortByQueueCreated = (a, b) => getQueueCreated(a) - getQueueCreated(b);

export const getAllWaitingTickets = (tickets, singleTicket = 0) => {
    // get waiting tickets
    const allWaitingTickets = Array.from(
        tickets.filter( a => Number(a.status) === 0 )
    );
    // get priority customers first and filter out null objects using boolean
    const waitingPriorityCustomers = allWaitingTickets.filter( a => a.priority ? a : null ).filter(Boolean);
    // sort priority customers by listOrder in descending order
    waitingPriorityCustomers.sort((a, b) => b.priority - a.priority);

    // remove the waitingPriorityCustomers from waitingTickets
    let waitingTickets = allWaitingTickets.filter( a => !a.priority );
    waitingTickets.sort(sortByQueueCreated);

    return [ ...waitingPriorityCustomers, ...waitingTickets];
}
export const getMyWaitingTickets = (tickets, authenticatedUser, supportDeskId) => {
    const userId = authenticatedUser.id;
    const services = authenticatedUser.services;
    const waitingTickets = [];

    // create a copy of tickets
    const ticketsCopy = Array.from(tickets);
    // find my active tickets
    const activeTickets = ticketsCopy
      .filter((a) => Number(a.status) === 1 && a.manage != null && a.manage.userId === userId);
    waitingTickets.push(...activeTickets);
    
    // find tickets that are not served yet and are matching my services
    const serviceWaitingTickets = ticketsCopy
      .filter((a) => (
        Number(a.status) === 0
        && services.includes(a.serviceId)
        && ( a.manage == null || ( a.manage != null && !Object.keys(a.manage).length) )
      ));
    waitingTickets.push(...serviceWaitingTickets);

    // find tickets that were sent to my counterId
    const counterWaitingTickets = ticketsCopy
      .filter((a) => (
        Number(a.status) === 0
        && a.manage != null
        && a.manage.supportDeskId === supportDeskId
      ));
    waitingTickets.push(...counterWaitingTickets);

    // find tickets that were sent to my userId
    const userWaitingTickets = ticketsCopy
      .filter((a) => (
        Number(a.status) === 0  
        && a.manage != null
        && a.manage.userId === userId
      ));
    waitingTickets.push(...userWaitingTickets);
    // Reissued tickets keep created for reporting, but use originalCreated for queue position.
    waitingTickets.sort(sortByQueueCreated);
    // get priority customers first and filter out null objects using boolean
    let waitingPriorityCustomers = waitingTickets.filter( a => a.priority ? a : null ).filter(Boolean)
    // sort priority customers by listOrder in descending order
    waitingPriorityCustomers.sort((a, b) => b.priority - a.priority);
    // remove the waitingPriorityCustomers from waitingTickets
    let waitingNonPriorityTickets = waitingTickets.filter( a => !a.priority );
    // combine the priority and non priority tickets
    return [...waitingPriorityCustomers, ...waitingNonPriorityTickets];
}

export const getManagementLockFromTicket = (ticket) => {
  // check what fields are in the ticket
  if(!ticket) return null;
  let payload;
  if(ticket.tracking != null && ticket.tracking.packageStatus == null) {
    payload = MANAGEMENT_LOCK.packageTracking
  } else if (ticket.tracking != null && ticket.tracking.packageStatus != null) {
    payload = MANAGEMENT_LOCK.default
  } else {
    payload = MANAGEMENT_LOCK.default
  } 
  return payload;
};
